import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';
import { EstadoError } from './EstadoError';

const dinero = (v) => Number(v || 0).toLocaleString('es-AR', { style:'currency', currency:'ARS' });
const fecha = (v) => {
  if (!v) return '—';
  const raw = String(v);
  const valor = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T12:00:00` : raw;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleDateString('es-AR');
};
const input = { padding:'10px 11px', border:'1px solid #cbd5e1', borderRadius:7, fontSize:14, boxSizing:'border-box' };
const btn = (bg, disabled=false) => ({ background:disabled?'#94a3b8':bg, color:'white', border:0, borderRadius:7, padding:'8px 11px', cursor:disabled?'not-allowed':'pointer', fontWeight:700, whiteSpace:'nowrap' });

const escaparHtml = (valor) => String(valor ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');
const numeroTermico = (valor) => Number(valor || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cantidadTermica = (valor) => Number(valor || 0).toLocaleString('es-AR', { maximumFractionDigits: 3 });

function prepararVentanaTermica() {
  const ventana = window.open('', '_blank', 'width=420,height=720');
  if (!ventana) return null;
  ventana.document.open();
  ventana.document.write('<!doctype html><html><body style="font-family:Arial;padding:20px">Preparando comprobante fiscal…</body></html>');
  ventana.document.close();
  return ventana;
}

function htmlComprobanteTermico(data, copia = true) {
  const c = data.comprobante || {};
  const e = data.emisor || {};
  const r = data.receptor || {};
  const importes = data.importes || {};
  const esNota = c.tipo === 'Nota de Crédito';
  const titulo = `${esNota ? 'NOTA DE CRÉDITO' : 'FACTURA'} ${escaparHtml(c.letra || '')}`;
  const fechaComprobante = c.fecha ? fecha(c.fecha) : '—';
  const fechaVto = c.cae_vto ? fecha(c.cae_vto) : '—';
  const inicio = e.inicio_actividades ? fecha(e.inicio_actividades) : '—';
  const filas = (data.items || []).map((item) => `
    <tr>
      <td class="cant">${cantidadTermica(item.cantidad)}</td>
      <td class="desc">${escaparHtml(item.descripcion || 'Artículo')}</td>
      <td class="importe">$${numeroTermico(item.subtotal)}</td>
    </tr>`).join('');
  const iva = c.letra === 'A'
    ? (importes.iva || []).map((item) => `<div class="linea"><span>IVA ${numeroTermico(item.alicuota).replace(',00','')}%</span><span>$${numeroTermico(item.importe)}</span></div>`).join('')
    : '';
  const asociado = c.asociado
    ? `<div class="centrado pequeno">Asociada a FC ${escaparHtml(c.asociado.letra)} ${String(c.asociado.punto_venta).padStart(5,'0')}-${String(c.asociado.numero).padStart(8,'0')}</div>`
    : '';
  const qr = data.qr_data_url ? `<img id="qr-arca" class="qr" alt="QR ARCA" src="${data.qr_data_url}"/>` : '';

  return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"><title>${titulo}</title>
<style>
  @page { size: 58mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { width: 58mm; margin: 0; padding: 0; color: #000; background: #fff; }
  body { font-family: "Courier New", monospace; font-size: 10.5px; line-height: 1.25; padding: 2.5mm 2mm 5mm; }
  h1 { font-size: 18px; margin: 0 0 2px; text-transform: uppercase; }
  h2 { font-size: 15px; margin: 4px 0; }
  p { margin: 2px 0; }
  .centrado { text-align: center; }
  .negrita { font-weight: 700; }
  .pequeno { font-size: 9px; }
  .divisor { border-top: 1px dashed #000; margin: 5px 0; }
  .doble { border-top: 2px solid #000; margin: 5px 0; }
  .letra { display: inline-block; border: 2px solid #000; font-size: 25px; font-weight: 700; min-width: 12mm; padding: 1px 4px; margin: 2px 0; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th, td { vertical-align: top; padding: 2px 0; }
  th { border-bottom: 1px solid #000; font-size: 9.5px; }
  .cant { width: 13%; text-align: center; }
  .desc { width: 57%; padding-right: 2px; overflow-wrap: anywhere; }
  .importe { width: 30%; text-align: right; }
  .linea { display: flex; justify-content: space-between; gap: 4px; margin: 2px 0; }
  .total { font-size: 16px; font-weight: 700; margin: 5px 0; }
  .qr { display: block; width: 34mm; height: 34mm; object-fit: contain; margin: 4px auto; image-rendering: pixelated; }
  .copia { font-weight: 700; letter-spacing: 1px; }
</style></head><body>
  <div class="centrado">
    ${e.logo_url ? `<img src="${escaparHtml(e.logo_url)}" alt="Logo" style="display:block;max-width:45mm;max-height:20mm;object-fit:contain;margin:0 auto 2mm;"/>` : ''}
    <h1>${escaparHtml(e.nombre_fantasia || 'CASA ALESSIO')}</h1>
    <p>Nombre de fantasía</p>
    <p class="negrita">Titular: ${escaparHtml(e.razon_social || '—')}</p>
    <p>CUIT: ${escaparHtml(e.cuit || '—')}</p>
    <p>${escaparHtml(e.direccion || '—')}</p>
    <p>${escaparHtml(e.condicion_iva || '—')}</p>
    ${e.ingresos_brutos && e.ingresos_brutos !== '—' ? `<p>IIBB: ${escaparHtml(e.ingresos_brutos)}</p>` : ''}
    ${inicio !== '—' ? `<p>Inicio actividades: ${inicio}</p>` : ''}
    ${copia ? '<p class="copia">COPIA / REIMPRESIÓN</p>' : ''}
  </div>
  <div class="doble"></div>
  <div class="centrado"><span class="letra">${escaparHtml(c.letra || '')}</span><h2>${titulo}</h2></div>
  <div class="centrado">P.V. ${String(c.punto_venta || 0).padStart(5,'0')} - N° ${String(c.numero || 0).padStart(8,'0')}</div>
  <div class="centrado">Cód. ${escaparHtml(c.tipo_codigo || '')} · ${fechaComprobante}</div>
  ${asociado}
  <div class="divisor"></div>
  <p class="negrita">RECEPTOR</p>
  <p>${escaparHtml(r.nombre || 'Consumidor Final')}</p>
  <p>CUIT/DNI: ${escaparHtml(r.documento || '0')}</p>
  <p>IVA: ${escaparHtml(r.condicion_iva || 'Consumidor Final')}</p>
  ${r.direccion && r.direccion !== '—' ? `<p>Domicilio: ${escaparHtml(r.direccion)}</p>` : ''}
  <div class="divisor"></div>
  <table><thead><tr><th class="cant">Cant</th><th class="desc">Detalle</th><th class="importe">Subtotal</th></tr></thead><tbody>${filas}</tbody></table>
  <div class="divisor"></div>
  ${c.letra === 'A' ? `<div class="linea"><span>Neto gravado</span><span>$${numeroTermico(importes.neto_gravado)}</span></div>${iva}${Number(importes.exento || 0) > 0 ? `<div class="linea"><span>Exento</span><span>$${numeroTermico(importes.exento)}</span></div>` : ''}` : ''}
  <div class="linea total"><span>TOTAL</span><span>$${numeroTermico(importes.total ?? c.total)}</span></div>
  <p>Pago: ${escaparHtml(c.metodo_pago || '—')}</p>
  <div class="doble"></div>
  ${qr}
  <div class="centrado">
    <p class="negrita">CAE: ${escaparHtml(c.cae || '—')}</p>
    <p>Vto. CAE: ${fechaVto}</p>
    <p class="pequeno">Comprobante autorizado electrónicamente por ARCA</p>
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.focus(); window.print(); }, 250);
    });
    window.addEventListener('afterprint', function () { window.close(); });
  </script>
</body></html>`;
}

export default function Facturacion() {
  const hoy = new Date().toISOString().slice(0,10);
  const hace30 = new Date(Date.now()-29*86400000).toISOString().slice(0,10);
  const [filtros,setFiltros] = useState({ inicio:hace30, fin:hoy, estado:'', busqueda:'' });
  const [filas,setFilas] = useState([]);
  const [resumen,setResumen] = useState({});
  const [error,setError] = useState('');
  const [cargando,setCargando] = useState(false);
  const [procesando,setProcesando] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const qs = new URLSearchParams(Object.entries(filtros).filter(([,v])=>v!==''));
      const [lista, resumenData] = await Promise.all([
        api.get(`/api/afip/comprobantes?${qs}`),
        api.get('/api/afip/resumen'),
      ]);
      setFilas(Array.isArray(lista)?lista:[]);
      setResumen(resumenData||{});
    } catch (err) {
      setError(err instanceof ApiError ? err.mensaje : 'No se pudo cargar el módulo de facturación.');
    } finally { setCargando(false); }
  }, [filtros]);

  useEffect(()=>{ cargar(); },[cargar]);

  const completarImpresionTermica = async (comprobanteId, ventana, copia) => {
    const datos = await api.get(`/api/afip/comprobantes/${comprobanteId}/termico`);
    ventana.document.open();
    ventana.document.write(htmlComprobanteTermico(datos, copia));
    ventana.document.close();
  };

  const imprimirTermico = async (comprobanteId, copia = true) => {
    const ventana = prepararVentanaTermica();
    if (!ventana) return alert('Habilitá las ventanas emergentes para imprimir el comprobante térmico.');
    setProcesando(`termico-${comprobanteId}`);
    try {
      await completarImpresionTermica(comprobanteId, ventana, copia);
    } catch (err) {
      ventana.close();
      alert('❌ '+(err instanceof ApiError ? err.mensaje : 'No se pudo preparar la impresión térmica.'));
    } finally {
      setProcesando(null);
    }
  };

  const emitirFactura = async (ventaId) => {
    if (!window.confirm(`¿Emitir la factura electrónica de la venta #${ventaId}?`)) return;
    const ventana = prepararVentanaTermica();
    setProcesando(`f-${ventaId}`);
    try {
      const r=await api.post('/api/afip/facturar',{venta_id:ventaId});
      await cargar();
      if (ventana) {
        await completarImpresionTermica(r.comprobante_id, ventana, false);
      } else {
        alert(`✅ Factura ${r.letra} ${String(r.punto_venta).padStart(5,'0')}-${String(r.numero).padStart(8,'0')} autorizada.\nCAE: ${r.cae}\nHabilitá las ventanas emergentes y usá “Imprimir” para obtener el ticket térmico.`);
      }
    } catch(err){
      if (ventana) ventana.close();
      alert('❌ '+(err instanceof ApiError?err.mensaje:'No se pudo emitir la factura.'));
    } finally{ setProcesando(null); }
  };

  const emitirNotaCredito = async (ventaId) => {
    const motivo=window.prompt('Motivo de la nota de crédito total:','Anulación total de la operación');
    if (motivo===null) return;
    if (motivo.trim().length<5) return alert('Ingresá un motivo descriptivo.');
    if (!window.confirm('La nota de crédito será por el total de la factura. ¿Continuar?')) return;
    const ventana = prepararVentanaTermica();
    setProcesando(`nc-${ventaId}`);
    try {
      const r=await api.post('/api/afip/nota-credito',{venta_id:ventaId,motivo:motivo.trim()});
      await cargar();
      if (ventana) {
        await completarImpresionTermica(r.comprobante_id, ventana, false);
      } else {
        alert(`✅ Nota de crédito ${r.letra} autorizada.\n${r.mensaje}\nHabilitá las ventanas emergentes para imprimirla.`);
      }
    } catch(err){
      if (ventana) ventana.close();
      alert('❌ '+(err instanceof ApiError?err.mensaje:'No se pudo emitir la nota de crédito.'));
    } finally{ setProcesando(null); }
  };

  const abrirPdf = async (id) => {
    setProcesando(`pdf-${id}`);
    try {
      const blob=await api.download(`/api/afip/comprobantes/${id}/pdf`);
      const url=URL.createObjectURL(blob);
      const enlace=document.createElement('a');
      enlace.href=url; enlace.target='_blank'; enlace.rel='noopener noreferrer';
      document.body.appendChild(enlace); enlace.click(); enlace.remove();
      setTimeout(()=>URL.revokeObjectURL(url),60000);
    } catch(err){ alert('❌ '+(err instanceof ApiError?err.mensaje:'No se pudo generar el PDF.')); }
    finally{ setProcesando(null); }
  };

  const probarConexion = async () => {
    setProcesando('test');
    try { const r=await api.get('/api/afip/test'); alert(`✅ ARCA respondió correctamente.\nAmbiente: ${r.ambiente}\n${r.tipo_consulta}: último N° ${r.ultimo_comprobante}`); }
    catch(err){ alert('❌ '+(err instanceof ApiError?err.mensaje:'No se pudo conectar con ARCA.')); }
    finally{ setProcesando(null); }
  };

  const totalMostrado=useMemo(()=>filas.reduce((a,f)=>a+Number(f.total||0),0),[filas]);
  if(error) return <EstadoError mensaje={error} onReintentar={cargar}/>;

  return <div style={{padding:20,background:'white',borderRadius:12,border:'1px solid #e2e8f0',minHeight:'80vh'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap',borderBottom:'2px solid #2563eb',paddingBottom:12}}>
      <div><h2 style={{margin:0}}>🏛️ Facturación electrónica</h2><small style={{color:'#64748b'}}>Facturas, notas de crédito, CAE, impresión térmica, PDF y control centralizado</small></div>
      <button onClick={probarConexion} disabled={procesando==='test'} style={btn('#475569',procesando==='test')}>🔌 Probar conexión ARCA</button>
    </div>

    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,margin:'18px 0'}}>
      <Kpi titulo="Pendientes" valor={resumen.pendientes||0} icono="⏳" color="#c2410c"/>
      <Kpi titulo="Facturadas" valor={resumen.facturadas||0} icono="✅" color="#047857"/>
      <Kpi titulo="Notas de crédito" valor={resumen.notas_credito||0} icono="↩️" color="#7c3aed"/>
      <Kpi titulo="Facturado hoy" valor={dinero(resumen.facturado_hoy)} icono="💵" color="#1d4ed8"/>
    </div>

    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10,background:'#f8fafc',padding:12,borderRadius:10,marginBottom:16}}>
      <label><small>Desde</small><input type="date" style={{...input,width:'100%'}} value={filtros.inicio} onChange={e=>setFiltros({...filtros,inicio:e.target.value})}/></label>
      <label><small>Hasta</small><input type="date" style={{...input,width:'100%'}} value={filtros.fin} onChange={e=>setFiltros({...filtros,fin:e.target.value})}/></label>
      <label><small>Estado</small><select style={{...input,width:'100%'}} value={filtros.estado} onChange={e=>setFiltros({...filtros,estado:e.target.value})}><option value="">Todos</option><option value="pendiente">Pendientes</option><option value="facturada">Facturadas</option><option value="nota_credito">Con nota de crédito</option><option value="anulada">Anuladas</option></select></label>
      <label><small>Buscar</small><input style={{...input,width:'100%'}} value={filtros.busqueda} onChange={e=>setFiltros({...filtros,busqueda:e.target.value})} placeholder="Cliente, CUIT o venta"/></label>
      <div style={{display:'flex',alignItems:'end'}}><button onClick={cargar} style={{...btn('#2563eb'),width:'100%'}}>🔄 Actualizar</button></div>
    </div>

    <div style={{overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',minWidth:1100}}>
      <thead><tr>{['Fecha','Venta','Cliente','Total','Estado','Factura','Nota de crédito','Acciones'].map(h=><th key={h} style={{textAlign:h==='Total'?'right':'left',padding:10,borderBottom:'2px solid #cbd5e1',fontSize:13}}>{h}</th>)}</tr></thead>
      <tbody>{filas.map(f=><tr key={f.venta_id} style={{borderBottom:'1px solid #e2e8f0'}}>
        <td style={{padding:10}}>{fecha(f.fecha)}</td>
        <td style={{padding:10,fontWeight:700}}>#{f.venta_id}<br/><small style={{color:'#64748b'}}>{f.metodo_pago}</small></td>
        <td style={{padding:10}}>{f.cliente_nombre}<br/><small>{f.cliente_cuit||'Consumidor final'}</small></td>
        <td style={{padding:10,textAlign:'right',fontWeight:800}}>{dinero(f.total)}</td>
        <td style={{padding:10}}><Estado valor={f.estado_facturacion}/></td>
        <td style={{padding:10}}>{f.factura_id?<><b>FC {f.factura_letra}</b><br/><small>{String(f.factura_punto_venta).padStart(5,'0')}-{String(f.factura_numero).padStart(8,'0')}</small></>:'—'}</td>
        <td style={{padding:10}}>{f.nota_credito_id?<><b>NC {f.nota_credito_letra}</b><br/><small>{String(f.nota_credito_punto_venta).padStart(5,'0')}-{String(f.nota_credito_numero).padStart(8,'0')}</small></>:'—'}</td>
        <td style={{padding:10}}><div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {!f.factura_id && f.estado_venta!=='Anulada' && <button disabled={Boolean(procesando)} onClick={()=>emitirFactura(f.venta_id)} style={btn('#2563eb',Boolean(procesando))}>Emitir factura</button>}
          {f.factura_id && <button disabled={Boolean(procesando)} onClick={()=>imprimirTermico(f.factura_id,true)} style={btn('#0f172a',Boolean(procesando))}>🖨️ Imprimir factura</button>}
          {f.factura_id && <button disabled={Boolean(procesando)} onClick={()=>abrirPdf(f.factura_id)} style={btn('#0f766e',Boolean(procesando))}>PDF factura</button>}
          {f.factura_id && !f.nota_credito_id && f.estado_venta!=='Anulada' && <button disabled={Boolean(procesando)} onClick={()=>emitirNotaCredito(f.venta_id)} style={btn('#7c3aed',Boolean(procesando))}>Nota crédito total</button>}
          {f.nota_credito_id && <button disabled={Boolean(procesando)} onClick={()=>imprimirTermico(f.nota_credito_id,true)} style={btn('#4c1d95',Boolean(procesando))}>🖨️ Imprimir NC</button>}
          {f.nota_credito_id && <button disabled={Boolean(procesando)} onClick={()=>abrirPdf(f.nota_credito_id)} style={btn('#6d28d9',Boolean(procesando))}>PDF NC</button>}
        </div></td>
      </tr>)}</tbody>
      <tfoot><tr><td colSpan="3" style={{padding:10,fontWeight:800}}>Mostradas: {filas.length}</td><td style={{padding:10,textAlign:'right',fontWeight:800}}>{dinero(totalMostrado)}</td><td colSpan="4"/></tr></tfoot>
    </table></div>
    {!cargando&&filas.length===0&&<p style={{textAlign:'center',color:'#94a3b8',padding:30}}>No hay operaciones para los filtros elegidos.</p>}
    {cargando&&<p style={{textAlign:'center',color:'#64748b',padding:20}}>Cargando comprobantes…</p>}
    <div style={{marginTop:18,padding:12,background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,fontSize:13,color:'#92400e'}}>
      Una venta con CAE solo puede devolverse después de emitir su nota de crédito electrónica. La devolución de stock y dinero se registra luego desde Historial de Ventas.
    </div>
  </div>;
}

function Kpi({titulo,valor,icono,color}){return <div style={{padding:14,border:'1px solid #e2e8f0',borderRadius:10,background:'white'}}><div style={{fontSize:12,color:'#64748b'}}>{icono} {titulo}</div><div style={{fontSize:22,fontWeight:800,color,marginTop:5}}>{valor}</div></div>}
function Estado({valor}){const mapa={Pendiente:['#ffedd5','#c2410c'],Facturada:['#dcfce7','#047857'],'Nota de crédito emitida':['#ede9fe','#6d28d9'],Anulada:['#fee2e2','#b91c1c']};const [bg,c]=mapa[valor]||['#e2e8f0','#334155'];return <span style={{background:bg,color:c,padding:'4px 8px',borderRadius:999,fontSize:12,fontWeight:700}}>{valor}</span>}
